// @flow

import { useEffect, useState, useCallback } from 'react';
import Logging from '@ta/common.logging';
import Radio from '@ta/common.radio';
import throttle from 'lodash/throttle';

const adsRadio = new Radio('ads');

export function isValidMobileSize(size: string) {
  return ['320x50', '300x250', '300x250-8x8', '8x8'].includes(size.toLowerCase());
}

export function injectAds(elemIds: string[], isNewContext: boolean = false) {
  Logging.debug('[ADS] Injecting ads', { elemIds, isNewContext });
  adsRadio.emit('inject', {
    elemIds,
    isNewContext,
  });
}

export function injectAd(adId: string, isNewContext: boolean = false) {
  injectAds([adId], isNewContext);
}

export function refreshAds(isNewContext: boolean = false) {
  Logging.debug('[ADS] Refreshing ads', { isNewContext });
  adsRadio.emit('refresh', isNewContext);
}

export type AdStatus = {
  isVisible?: boolean,
  isRendered: boolean,
};

export type AdUpdateEvent = AdStatus & {
  id: string,
  eventType: 'SlotRenderEnded' | 'SlotOnload',
  originalEvent: any, // https://developers.google.com/doubleclick-gpt/reference#googletag.events.event
};

/**
 * @example useAdStatus('gpt-id-300x250-inline1', console.debug)
 * @example const { isVisible } = useAdStatus('gpt-id-300x250-inline1')
 * TODO: ignore -ref suffix
 */
export function useAdStatus(adId: string, callback?: (event: AdUpdateEvent) => any) {
  const [adStatus, setAdStatus] = useState<AdStatus>({
    isRendered: false,
  });

  useEffect(() => {
    const onAdUpdate = (event: AdUpdateEvent) => {
      const { id, isRendered, isVisible } = event;
      if (id === adId) {
        Logging.debug('[ADS] Ad updated', event);
        setAdStatus({ isRendered, isVisible });
        if (callback) callback(event);
      }
    };

    adsRadio.on('ad-update', onAdUpdate);
    return () => {
      adsRadio.off('ad-update', onAdUpdate);
    };
  });

  return adStatus;
}

/* eslint-disable no-restricted-globals */
export function useWindowSize() {
  const [size, setSize] = useState({
    width: undefined,
    height: undefined,
  });

  const updateSize = useCallback(
    throttle(() => {
      setSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    }, 100),
    [setSize],
  );

  useEffect(() => {
    updateSize();
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, [updateSize]);

  return size;
}
/* eslint-enable no-restricted-globals */
